import { FC, useCallback, useContext, useEffect, useState } from "react";

import { Button } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Subtitle } from "capitalrx-components-2.0/lib/typography";
import { Form, SubmitButton, Textbox, UseFormReturn } from "capitalrx-components-2.0/lib/forms";
import { QuestionnaireListResponse, states } from "capitalrx-types";

import { PACaseCreateForm } from "@/components";
import { ThemeSelectorContext } from "@/context";
import { getThemeButton, validatePhoneNumber } from "@/utils";


export interface PAQuestionnaireForm {
  readonly [key: string]: string | boolean | null;
}

interface FormProps {
  goToNextStep: Function;
  form: UseFormReturn<PACaseCreateForm>;
  questionnaireForm: UseFormReturn<PAQuestionnaireForm>;
  onBack: Function;
}


const CaseDraftForm: FC<FormProps> = ({ goToNextStep, form, questionnaireForm, onBack }) => {
  const { primary_color } = useContext(ThemeSelectorContext);

  const [questionnaires, _] = useState<QuestionnaireListResponse>(form.getValues("questionnaires"));

  const { six_six_twelve_columns } = useResponsive({
    six_six_twelve_columns: {
      desktop: 6,
      tablet: 6,
      mobile: 12,
    },
  });

  const { phone_fax_columns } = useResponsive({
    phone_fax_columns: {
      desktop: 3,
      tablet: 3,
      mobile: 12,
    },
  });

  useEffect(() => {
    const parentValues = form.getValues();
    const stateAbbreviation = states[parentValues.provider_state] ?? parentValues.provider_state ?? "";
    questionnaireForm.setValue(
      "provider_first_name",
      parentValues.provider_first_name ?? "",
    );

    questionnaireForm.setValue(
      "provider_last_name",
      parentValues.provider_last_name ?? "",
    );

    questionnaireForm.setValue(
      "provider_phone",
      parentValues.provider_phone ?? "",
    );

    questionnaireForm.setValue(
      "provider_fax",
      parentValues.provider_fax ?? "",
    );

    questionnaireForm.setValue(
      "provider_email",
      parentValues.provider_email ?? "",
    );

    /*
    I added back the prescriber address prefill to account for the recent
    scope adjustment to add prescriber address to the form, like the
    existing PA Case Form.
    */
    questionnaireForm.setValue(
      "provider_address",
      parentValues.provider_address ?? "",
    );

    questionnaireForm.setValue(
      "provider_address_2",
      parentValues.provider_address_2 ?? "",
    );

    questionnaireForm.setValue(
      "provider_city",
      parentValues.provider_city ?? "",
    );

    questionnaireForm.setValue(
      "provider_state",
      stateAbbreviation,
    );

    questionnaireForm.setValue(
      "provider_zip",
      parentValues.provider_zip ?? "",
    );
  }, [form, questionnaireForm]);

  const formSubmitted = useCallback(async (data: PAQuestionnaireForm) => {
    form.setValue("provider_first_name", data.provider_first_name as string);
    form.setValue("provider_last_name", data.provider_last_name as string);
    form.setValue("provider_phone", data.provider_phone as string);
    form.setValue("provider_fax", data.provider_fax as string);
    form.setValue("provider_email", data.provider_email as string);
    await goToNextStep();
  }, [questionnaires]);

  const goBack = useCallback(() => {
    onBack();
  }, [onBack]);

  return (
    <Form form={questionnaireForm} onSubmit={formSubmitted}>
      <div style={{ marginTop: "32px" }} />
      <Stack spacing="large">
        <Stack>
              <Subtitle>Prescriber Contact</Subtitle>
              <Grid>
                <Grid.Span columns={six_six_twelve_columns}>
                  <Textbox.Field
                    label="First Name"
                    name="provider_first_name"
                    required
                  />
                </Grid.Span>

                <Grid.Span columns={six_six_twelve_columns}>
                  <Textbox.Field
                    label="Last Name"
                    name="provider_last_name"
                    required
                  />
                </Grid.Span>
              </Grid>

              <Grid>
                <Grid.Span columns={phone_fax_columns}>
                  <Textbox.Field
                    label="Phone Number"
                    name="provider_phone"
                    validator={validatePhoneNumber}
                    required
                    instructions="123-456-7890"
                  />
                </Grid.Span>

                <Grid.Span columns={phone_fax_columns}>
                  <Textbox.Field
                    label="Fax Number"
                    name="provider_fax"
                    validator={validatePhoneNumber}
                    required
                    instructions="123-456-7890 x 123"
                  />
                </Grid.Span>

                <Grid.Span columns={six_six_twelve_columns}>
                  <Textbox.Field
                    label="Email Address"
                    name="provider_email"
                    instructions="email@address.com"
                  />
                </Grid.Span>
              </Grid>
        </Stack>
        <Shelf spacing="small">
          {!questionnaireForm.formState.isSubmitting && (
            <Button appearance={getThemeButton("secondary", primary_color)} onClick={goBack}>
              Back
            </Button>
          )}
          <SubmitButton appearance={getThemeButton("primary", primary_color)}>
            Send Draft
          </SubmitButton>
          <Shelf.Spacer />
        </Shelf>
      </Stack>
    </Form>
  );
};

export default CaseDraftForm;
